import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiToast } from '@hooks/useApiToast';
import { bffApiClient } from '@api/bff/client';
import { WorkOrdersApi } from '@api/bff/openapi';
import type { WorkOrderViewModel } from '@api/bff/openapi';
import { workOrderKeys } from '../constants';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';

type IncompleteWorkOrderParams = {
  jobNumber: string;
  subStatus?: string;
};

export const useIncompleteWorkOrder = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<WorkOrderViewModel, Error, IncompleteWorkOrderParams>({
    mutationFn: async ({ jobNumber, subStatus }: IncompleteWorkOrderParams) => {
      if (!navigator.onLine) {
        await db.transaction(
          'rw',
          [db.cachedWorkOrders, db.syncQueue],
          async () => {
            const cached = await db.cachedWorkOrders
              .where('jobNumber')
              .equals(jobNumber)
              .first();

            if (cached) {
              await db.cachedWorkOrders.update(jobNumber, {
                status: 'Incomplete' as never
              });
            }

            await enqueue({
              entityType: 'wo_incomplete',
              entityKey: jobNumber,
              payload: {
                jobNumber,
                subStatus,
                originalState: cached?.status
              },
              priority: 60
            });
          }
        );

        return {
          jobNumber,
          status: 'Incomplete'
        } as unknown as WorkOrderViewModel;
      }

      const api = new WorkOrdersApi(undefined, undefined, bffApiClient);
      const response = await api.apiWorkOrdersJobNumberIncompletePost({
        jobNumber,
        incompleteWorkOrderCommand: { subStatus: subStatus || undefined }
      });
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.byJobNumber(variables.jobNumber)
      });
      queryClient.invalidateQueries({
        queryKey: workOrderKeys.odata()
      });

      showSuccess('unlock.success', 'workOrders');
      onSuccessCallback?.();

      if (!navigator.onLine) {
        processAll();
      }

      if (navigator.onLine) {
        try {
          const metadata = await db.downloadMetadata.get(
            String(variables.jobNumber)
          );
          if (metadata?.status === 'cached') {
            await db.cachedWorkOrders.update(String(variables.jobNumber), {
              status: 'Incomplete' as never
            });
          }
        } catch {
          // Best effort
        }
      }
    },
    onError: (error, _variables) => {
      showError('unlock.error', error, 'workOrders');
    }
  });
};
